/**
 * PriceSummaryBand — the supplier-dossier price section summary band (Lane 134).
 *
 * Spec: docs/design/supplier-dossier-price-section-spec.md (v2, decisions
 * resolved). Evidence: docs/audits/2026-07-15_lane133_number-reconciliation_findings.md.
 *
 * The one test the band must pass (spec header): no single number can be read
 * in isolation and leave you with the wrong impression. Four tiles — the four
 * measurement families every dossier price number belongs to — plus the §5
 * rule-driven conditional prose (computed in utils/priceSectionNotes.js, fixed
 * patterns only, Rule 116).
 *
 * LANE 336 (R3-DOSSIER, Mega QA round 3, 2026-07-23): the window-direction
 * narration is REMOVED per Ryan ("makes it more confusing, just get rid of
 * it") — the band no longer renders the "what happened" blurb, and the two
 * Price-vs-Market tiles carry no explanatory notes. The engine's surviving
 * prose is the R2 scorecard note (scorecard tile) and the Lane 138 history-gap
 * disclosure (dollar tile) — both different concerns, both kept.
 *
 *   2.1 What their price changes cost you   — THE CANONICAL NUMBER (YoY family,
 *       dollars only since Lane 137: the Impact % was deleted here so this tile
 *       states its basis instead, rather than an unlabelled restatement of the
 *       trend tiles' own percentage)
 *   2.2 From the quarterly scorecard        — the scorecard file's own figure,
 *       PLUS (QA-3 item 2, Mega QA 2026-07-22) a clearly-labelled "Wingman
 *       measured" companion using the SAME PO-derived YoY % Lane 137 removed
 *       from 2.1 — here it is a deliberate, labelled scorecard-vs-Wingman
 *       comparison, not the unlabelled duplicate Lane 137 removed
 *   2.3 Price change vs market — last 12 months
 *   2.4 Price change vs market — last 24 months
 *
 * LANE 138 — the dollar tile follows the panel's horizon selector.
 * Before: the tile was pinned to 12 months while the YoY panel four inches below
 * honoured the selector, so at 24mo the band read +$44,985 against the panel's
 * +$202,213 — the same page stating two different figures for what this
 * supplier's price changes cost, which is the exact collision the band exists to
 * remove. The tile now reads the SAME response object the panel renders
 * (`priceImpact`), so the two agree by construction rather than by coincidence,
 * and its label names the horizon.
 *
 * The trend tiles deliberately do NOT follow the selector: they stay at 12 and
 * 24 and render together, per spec §2.4 ("two points on a timeline, not
 * alternatives") — that decision is itself the fix for the tab-flip confusion.
 * The consequence, accepted by Ryan (Option B, 2026-07-16): at a 6-month
 * selection the dollar tile shows a horizon no trend tile mirrors. Every tile
 * names its own window, so the band stays readable; the alternatives were to
 * strip 6mo from a selector shared with the panel and the PPI chart (a fix
 * travelling further than the bug) or to collapse the trend pair back into one
 * selector-driven tile (reinstating the tab-flip confusion).
 *
 * Everything below the band (YoY panel, Price vs Market card + charts, POs)
 * stays exactly as today (spec §4) — this is a summary, not a replacement.
 *
 * Rendering rules:
 *   - The band renders only on v2 responses (summary.impact_pct et al. exist
 *     only on the PRICE_IMPACT_V2_ENABLED path — ON on PROD + staging). With
 *     v2 OFF the dossier is byte-identical to today (no band).
 *   - The scorecard tile is ABSENT (not empty) when scorecard UI is disabled
 *     for the tenant — same three-layer invisibility rule as the Performance
 *     tab (§5.BT).
 *   - Every missing figure renders a positive "not enough history / none on
 *     file" line, never a blank and never a fabricated value.
 */
import {
  priceChangeStatusMessage,
  priceChangeAbsence,
  priceChangeLowCoverageLine,
} from '../utils/priceChangeStatus';
import React from 'react';
import MetricInfo from './MetricInfo';
import ForwardRunRateLine from './ForwardRunRateLine';
import {
  fmtSignedPct,
  fmtSignedDollars,
  formatPriorWindowGap,
} from '../utils/priceSectionNotes';
import { pluralize } from '../utils/pluralize';

// Lane 215 (Linear rebuild, Stage 2 — dossier child chrome): the band reads
// the `.sd` Linear token layer (var(--panel)/--border/--hair/--text*/--up/
// --down) so it flips with the shell in the future dark lane. Presentation
// only — tiles, notes, data, and testids are unchanged.
const LINEAR_PANEL = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow)',
};

const LABEL_STYLE = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--text-3)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--letter-spacing-wide)',
  marginBottom: '8px',
};

const VALUE_STYLE = {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--text)',
  fontFamily: 'monospace',
  lineHeight: 1.2,
};

const SUBVALUE_STYLE = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-3)',
  marginTop: '4px',
  lineHeight: 1.4,
};

const EMPTY_STYLE = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-3)',
  lineHeight: 1.5,
  padding: '2px 0',
};

const NOTE_STYLE = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-2)',
  lineHeight: 1.5,
  marginTop: '8px',
  paddingTop: '8px',
  borderTop: '1px solid var(--hair)',
};

const WORDS_STYLE = {
  ...NOTE_STYLE,
  color: '#92400E', // amber-900 — a caveat, not an error (kept: --warn fails AA at 11px)
};

// Lane 1414: the low-coverage flag is a caveat line, so it takes the same amber.
const FLAG_STYLE = {
  ...SUBVALUE_STYLE,
  color: '#92400E',
};

function Tile({ label, metricKey, metricValues, children, note, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        // QA R2-B (Mega QA 2 2026-07-22): the Lane-226 blue left-accent tab on the
        // 2.1 dollar tile is removed -- all four tiles read evenly, no accent bar.
        padding: '14px 16px',
        borderRadius: '8px',
        border: '1px solid var(--border)',
        background: 'var(--panel)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={LABEL_STYLE}>
        <MetricInfo metricKey={metricKey} values={metricValues}>{label}</MetricInfo>
      </div>
      <div style={{ flex: 1 }}>{children}</div>
      {note && <div style={NOTE_STYLE}>{note}</div>}
    </div>
  );
}

/**
 * Props:
 *   summarySelected — the `summary` object from the SELECTED-horizon dossier
 *                response (v2 shape: total_dollar_impact, impact_pct,
 *                spend_coverage_pct, materials_compared), i.e. the identical
 *                object the YoY panel renders. Null while loading / on failure.
 *   selectedMonths — the horizon `summarySelected` actually covers (6/12/24).
 *                MUST be the window of the held response, never the raw selector
 *                state: the selector flips on click while the fetch is still in
 *                flight, and captioning 12-month dollars "last 24 months" for
 *                one round trip is the very defect this lane closes.
 *   priorWindowGap — the response's additive `prior_window_gap` echo, present
 *                only when this window's prior period reaches back before the
 *                supplier's PO history (Lane 138). Null/absent = no disclosure.
 *   isV2       — the response's price_impact_v2 flag; band renders nothing when
 *                false/absent.
 *   blend12    — the 12-month ppi_blend (or null when the PPI profile is not
 *                configured / fetch failed). Fixed at 12 — see the header note.
 *   blend24    — the 24-month ppi_blend (same semantics). Fixed at 24.
 *   ppiConfigured — false when the supplier has no PPI benchmark profile at
 *                all (the dossier card shows its own "not configured" state).
 *   scorecard  — one of:
 *                  { state: 'hidden' }  — scorecard UI disabled: tile ABSENT
 *                  { state: 'none' }    — no scorecard rows for this supplier
 *                  { state: 'loading' }
 *                  { state: 'ready', pct, quarterLabel, status }
 *   priceChangeStatus - the SELECTED-horizon response's `price_change_status`.
 *   status12 / status24 - Lane 1414: the `price_change_status` of the SAME
 *                responses blend12 / blend24 come from. Only read when the
 *                server's low-coverage switch is on (see utils/priceChangeStatus).
 */
export default function PriceSummaryBand({ summarySelected, selectedMonths, priorWindowGap,
                                           isV2, blend12, blend24, ppiConfigured, scorecard,
                                           priceChangeStatus, status12, status24 }) {
  if (!isV2 || !summarySelected) return null;

  // The horizon the dollar tile is describing. Falls back to the selector's own
  // default rather than rendering an unlabelled window.
  const months = typeof selectedMonths === 'number' && selectedMonths > 0 ? selectedMonths : 12;

  const sc = scorecard || { state: 'hidden' };
  const showScorecardTile = sc.state !== 'hidden';

  const blendUsable = (b) => b && b.confidence !== 'insufficient';
  const b12 = blendUsable(blend12) ? blend12 : null;
  const b24 = blendUsable(blend24) ? blend24 : null;

  // Round 4 (D3): the typed canonical status outranks figure presence. When
  // the owner says honestly_insufficient, no figures render even if legacy
  // figures survived serialization (the measured=False path preserves them).
  const insufficientMessage = priceChangeStatusMessage(priceChangeStatus, selectedMonths);
  const hasYoy = !insufficientMessage
    && typeof summarySelected.impact_pct === 'number'
    && (summarySelected.materials_compared || 0) > 0;

  // P-3 (Lane 357): the `computePriceSectionNotes` CALL is removed from this
  // component. Lane 336 retired the eleven R1 narration patterns, leaving the
  // R2 scorecard note as the band's only rendered output from that engine; this
  // lane removes that render too (Ryan: "we don't need that, come on"), so the
  // call had no consumer left and would have sat as an unused binding.
  //
  // THE ENGINE ITSELF IS UNTOUCHED — both twins, and the frozen parity case
  // table. It is not dead code: `helpers/context.py`'s realized-YoY chat rider
  // calls PRICE_PATTERNS['note_scorecard'] directly (so the pattern is live on
  // the chat surface), the R1 flag still arms that rider, and the JS side stays
  // under `priceSectionNotes.test.js` + the parity gate. Deleting the rule would
  // break chat; this is a render change only. `formatPriorWindowGap` below is
  // still called — the Lane 138 history-gap disclosure stays on tile 2.1.

  // Lane 138: the history-gap disclosure. Rendered on the dollar tile because
  // that tile is where the truncated comparison surfaces as a single number; the
  // YoY panel renders the same sentence beside its own window label, from this
  // same helper, so the two cannot drift apart.
  //
  // Gated on hasYoy: the sentence ends "...so the earlier average covers a
  // shorter span", which is FALSE when there is no comparison at all —
  // there is no earlier average at all. A supplier whose window is empty
  // *because of* the same history gap therefore says nothing here; explaining
  // the empty case is a filed follow-up (Ryan, 2026-07-16), not this lane, and a
  // half-true sentence is worse than the silence it would replace.
  const gapNote = hasYoy ? formatPriorWindowGap(priorWindowGap) : null;

  // Lane 1414: the flag replaces the basis line below (it states the same parts
  // and share), so the tile never prints two coverage figures. Null = today.
  const yoyLowCoverage = hasYoy ? priceChangeLowCoverageLine(priceChangeStatus) : null;

  const fmtPts = (v) =>
    typeof v === 'number' && Number.isFinite(v) ? `${v >= 0 ? '+' : ''}${v.toFixed(1)} pts` : 'Not available';

  // ── P-3 (Lane 357): the FORWARD run-rate, the point-to-point %'s paired
  // dollar (round-4 execution plan §0 + pairing rules P3/P6).
  //
  // The key read is `forward_run_rate_surface_usd`, which the engine emits ONLY
  // for the negotiation case — sign-agreeing with the headline % AND a cost
  // increase. A sign-disagreeing dollar and the both-negative "we'll save money
  // next year" projection (R6.1: "if the price is at its lowest level… you're
  // NOT gonna surface that") are STRUCTURALLY ABSENT, so there is no branch here
  // that could render one. Never read `forward_run_rate_usd` (the locked
  // signs-agree verdict) or `forward_run_rate_detail_usd` (detail-view only) on
  // a pairing surface.
  //
  // Absent key ⇒ nothing renders, which is also the byte-identical flag-OFF
  // state (the legacy basis emits none of these keys at all).
  const fwdSurface = (b) =>
    b && typeof b.forward_run_rate_surface_usd === 'number'
      ? b.forward_run_rate_surface_usd
      : null;

  // Lane 336: the trend tiles carry NO note — the window-direction explainer
  // paragraphs that used to render here were removed per Ryan (R3-DOSSIER).
  const trendTile = (b, status, months, metricKey, emptyLine, testId) => {
    const fwd = fwdSurface(b);
    // Lane 1414: null unless the server's low-coverage switch is on, so with
    // it off both branches below are unreachable and the tile is unchanged.
    const absence = priceChangeAbsence(status);
    const lowCoverage = priceChangeLowCoverageLine(status);
    // LANE 382 item 2 — the basis parenthetical is GONE from the tile.
    //
    // P-3 rendered the construction inline: "you +1.2% (current level vs window
    // start), market +6.8%". Ryan (staging QA 2026-07-25) asked what that meant
    // ("does that mean it's snapped to the month?") and then answered his own
    // question about where it belongs: "it just gets too cluttery when you try
    // to explain everything. You can write that a little bit better in the
    // little information bubble — keep it short like it is now, but put that in
    // there. You can just say: you +1.2 versus market +6.8".
    //
    // So the tile states the two percentages and nothing else, and the
    // explanation moved into this tile's MetricInfo hover (the `short` of
    // price_band_trend_12mo / _24mo, revised in the same commit): "Their latest
    // price level against N complete months earlier, versus the market index
    // over the same months." That answers the month-snapping question in the
    // one place a reader goes looking for it, without a parenthetical the eye
    // has to step over on every read.
    //
    // The hover wording is deliberately basis-agnostic — both live
    // constructions compare an end level to a window-start level — so no
    // `price_basis` branch is needed here any more.
    return (
      <Tile
        // Lane 226: label tightened (was "Price change vs market — last N months");
        // the full definition lives in the MetricInfo hover (info kept).
        label={`Price vs market · ${months} mo`}
        metricKey={metricKey}
        metricValues={b ? {
          coverage_pct: b.spend_coverage_pct,
          materials_included: b.materials_included,
          materials_total: b.materials_total,
        } : undefined}
        testId={testId}
      >
        {!ppiConfigured ? (
          <div style={EMPTY_STYLE}>No market benchmark configured for this supplier.</div>
        ) : b && absence ? (
          // No buyer price change, so no gap and no forward dollar; the market
          // side stands on its own readiness.
          <>
            <div style={EMPTY_STYLE}>{absence}</div>
            <div style={SUBVALUE_STYLE}>market {fmtSignedPct(b.weighted_ppi_change_pct)}</div>
          </>
        ) : b ? (
          <>
            <div style={VALUE_STYLE}>{fmtPts(b.gap_pct)}</div>
            {/* Lane 382 item 2: the two percentages, uncluttered. The basis
                parenthetical QA R2-B DECISION 1 put here is now in the hover. */}
            <div style={SUBVALUE_STYLE}>
              you {fmtSignedPct(b.your_weighted_price_change_pct)}, market {fmtSignedPct(b.weighted_ppi_change_pct)}
            </div>
            {lowCoverage && <div style={FLAG_STYLE}>{lowCoverage}</div>}
            {/* P-3: the paired forward dollar — what that % means in money if
                today's prices hold. Labelled a PROJECTION so it can never be
                read as the recorded cost (that is tile 2.1, labelled history).
                Lane 382 item 3: rendered through the SHARED ForwardRunRateLine,
                the one owner of this sentence + its red — SupplierDetail's
                Price vs Market card renders the identical component, so the two
                surfaces can no longer say the same thing two ways. */}
            <ForwardRunRateLine
              usd={fwd}
              months={months}
              style={SUBVALUE_STYLE}
              testId={`${testId}-forward`}
            />
          </>
        ) : (
          <div style={EMPTY_STYLE}>{emptyLine}</div>
        )}
      </Tile>
    );
  };

  return (
    <div style={{ ...LINEAR_PANEL, padding: '16px', marginBottom: '24px' }}>
      <div data-testid="price-summary-band">
      <div style={{ ...LABEL_STYLE, marginBottom: '14px' }}>
        Price Summary
      </div>

      {/* Lane 336: the conditional "what happened" blurb (spec §1) is removed —
          the window-direction narration was Ryan-ordered out (R3-DOSSIER). */}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${showScorecardTile ? 4 : 3}, 1fr)`,
          gap: '12px',
        }}
      >
        {/* 2.1 — THE CANONICAL NUMBER. Lane 138: follows the panel's horizon
            selector and names that horizon, so band and panel always agree. */}
        <Tile
          // Lane 226: label tightened (was "What their price changes cost you —
          // last N months"); the MetricInfo hover keeps the full definition.
          // QA R2-B: the `primary` left-accent was removed (see Tile above).
          //
          // P-3 (Lane 357): "so far" is LOAD-BEARING, not padding — pairing rule
          // P4 (Lane-345 recon §5) requires the backward dollar to carry an
          // explicit history label wherever it can be read beside the
          // point-to-point %, and the trend tiles two columns over now render a
          // FORWARD dollar. Without the token a reader has two dollar figures on
          // one band with nothing distinguishing recorded cost from projection —
          // the exact collision this band exists to remove. Test-pinned.
          label={`What price changes cost you so far · ${months} mo`}
          metricKey="price_band_yoy_cost"
          metricValues={hasYoy ? {
            materials_compared: summarySelected.materials_compared,
            coverage_pct: summarySelected.spend_coverage_pct,
            months,
          } : { months }}
          note={gapNote}
          testId="price-band-yoy"
        >
          {hasYoy ? (
            <>
              <div style={{ ...VALUE_STYLE, color: summarySelected.total_dollar_impact > 0 ? 'var(--up)' : summarySelected.total_dollar_impact < 0 ? 'var(--down)' : 'var(--text)' }}>
                {fmtSignedDollars(summarySelected.total_dollar_impact)}
              </div>
              {/* Lane 137: the "+9.3% on comparable spend" duplicate percentage
                  is DELETED (the trend tiles carry the page's one percentage);
                  this line states the dollar figure's basis instead. The count
                  is materials_compared (matched materials only) — NEVER the
                  panel row count, which includes new + discontinued rows that
                  produce no comparison.
                  Lane 138 (Ryan, load-bearing): this subline renders at EVERY
                  horizon, not just 12. It is the thing standing between a thin
                  figure and a reader treating it as solid — at 6 months Parker
                  Hannifin - Css rests on 2 comparable materials and ~$2.8K, and
                  this line is what says so. */}
              {/* ✅ D71 (Ryan, 2026-08-09), Option 1 of three: NAME THE MEASURE,
                  keep both numbers. "of your spend with them" was the phrase the
                  supplier dossier ALSO used for a different denominator one panel
                  away (`SupplierDetail.js:1444`, the market-index share), so the
                  two read as one claim while differing by tens of points on the
                  same supplier. This one is the share of spend on materials
                  bought in BOTH halves of the window -- the only spend a price
                  change can be computed on, and therefore the basis of the dollar
                  figure printed above.
                  ⛔ THE NUMBER DOES NOT MOVE. Option 3 (one engine, one figure) is
                  the Rule 120 answer and was explicitly NOT taken today; only the
                  words changed. */}
              {yoyLowCoverage ? (
                <div style={FLAG_STYLE}>{yoyLowCoverage}</div>
              ) : (
              <div style={SUBVALUE_STYLE}>
                across {pluralize(summarySelected.materials_compared, 'comparable material')}
                {typeof summarySelected.spend_coverage_pct === 'number'
                  ? `, ${Math.round(summarySelected.spend_coverage_pct)}% of your spend has a comparable prior purchase`
                  : ''}
              </div>
              )}
            </>
          ) : (
            <div style={EMPTY_STYLE}>
              {insufficientMessage || 'Not enough matched purchase history for a comparison over this window.'}
            </div>
          )}
        </Tile>

        {/* 2.2 — the scorecard file's figure (tile absent when scorecard UI is
            disabled for the tenant; see component docstring) */}
        {showScorecardTile && (
          <Tile
            // Lane 226: label tightened (was "From the quarterly scorecard").
            label="Quarterly scorecard"
            metricKey="price_band_scorecard"
            // LANE 382 item 1 — the Wingman-divergence companion is GONE from
            // this tile entirely, hover included. Ryan (staging QA 2026-07-25):
            // "you can get rid of the Wingman divergence on there for
            // calculating it differently, so don't worry about that, because
            // the quarterly scorecards aren't necessarily as up to date. It's
            // fine."
            //
            // History, so nobody re-adds it a third time: QA-3 item 2 (Lane 300)
            // RENDERED a "Wingman PO-measured (YoY): +11.0%" subline here;
            // P-3 (Lane 357) moved it into this hover as the declared numeric
            // slot `impact_pct` rather than deleting it. Ryan has now called the
            // comparison itself unnecessary, so both the slot and its clause in
            // the registry `short` are removed (metricExplanations.json, same
            // commit) and no `metricValues` is passed — offering a value for an
            // undeclared slot would trip MetricInfo's allowlist warning.
            //
            // The tile therefore states the scorecard file's own figure and
            // nothing else. The one-quarter-vs-rolling-year WINDOW caveat stays
            // in the hover prose: that is what keeps the scorecard figure from
            // reading as a contradiction of tile 2.1, and it is a different
            // thing from the divergence Ryan retired.
            // P-3: `note={notes.noteScorecard}` REMOVED — the redundant
            // "Your purchase orders show +$41,422 of price increases over the
            // last 12 months." sentence, restating tile 2.1's own dollar one
            // column over (Ryan: "we don't need that, come on").
            //
            // The RULE ENGINE IS DELIBERATELY UNTOUCHED, both twins. This is NOT
            // a Lane 149/336 lockstep removal: the chat realized-YoY rider calls
            // PRICE_PATTERNS['note_scorecard'] DIRECTLY (helpers/context.py), so
            // deleting the pattern would break chat, and the parity coverage gate
            // asserts every Python pattern is reachable from the frozen case
            // table — not that a component renders it. Render change only;
            // priceSectionNotes.js / price_section_notes.py / the case table all
            // stay byte-identical and every parity layer stays green.
            testId="price-band-scorecard"
          >
            {sc.state === 'ready' && typeof sc.pct === 'number' ? (
              <>
                <div style={VALUE_STYLE}>
                  {fmtSignedPct(sc.pct)}
                  {sc.quarterLabel ? (
                    <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-regular)', color: 'var(--text-3)', marginLeft: '6px' }}>
                      ({sc.quarterLabel})
                    </span>
                  ) : null}
                </div>
                {/* P-3 (Lane 357) — RELEGATED TO THE HOVER, not deleted.
                    QA-3 item 2 (Mega QA 2026-07-22) added a rendered
                    "Wingman PO-measured (YoY): +11.0%" companion here so a
                    reader could see whether the scorecard's own figure matched
                    what Wingman measured from purchase orders. That comparison
                    is still worth having — but rendering it put a THIRD
                    supplier-level percentage on this band, inches from the
                    scorecard's own % and the two trend tiles' "you" figures,
                    which is precisely the 11.0-vs-12.4 confusion Ryan flagged.

                    Pairing rule P5 (Lane-345 recon §5): the averages-based %
                    never renders as a headline — MetricInfo only, labelled. The
                    value is now passed to this tile's MetricInfo as a declared
                    numeric slot (see `metricValues` above) and reads inside the
                    hover, so the comparison survives on demand while the band
                    itself carries exactly one percentage per tile. */}
                {sc.status === 'partial_coverage' && (
                  <div style={WORDS_STYLE} data-testid="scorecard-unverified-words">
                    Wingman could not verify this figure from your purchase orders.
                  </div>
                )}
              </>
            ) : sc.state === 'loading' ? (
              <div style={EMPTY_STYLE}>Loading…</div>
            ) : (
              <div style={EMPTY_STYLE}>No scorecard on file for this supplier.</div>
            )}
          </Tile>
        )}

        {/* 2.3 / 2.4 — the market-trend windows, both on screen (spec §2.4:
            two points on a timeline, not alternatives). Lane 336: no notes. */}
        {trendTile(
          b12, status12, 12, 'price_band_trend_12mo',
          'Not enough purchase history for a market comparison in this window.',
          'price-band-trend-12'
        )}
        {trendTile(
          b24, status24, 24, 'price_band_trend_24mo',
          'Not enough purchase history for a two-year view.',
          'price-band-trend-24'
        )}
      </div>
      </div>
    </div>
  );
}
